import * as React from "react";

import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord.ts";
import { miscContextForBoard } from "@/shared/domain/agentThread/requestsApplyTo";
import { AssociationType } from "@/shared/domain/association";
import {
  getLiveVisualizerState,
  useViewingContext,
  ViewingContext,
} from "@/shared/services/ViewingContextService";

import { ContextType } from "../contextType";
import { withoutCanvasItem } from "../contextUtils";

/**
 * Maps IDs to human-readable display names for the context widget.
 *
 * Devices are absent by design: a device's id is user-chosen and is the only
 * name it has, so there is nothing to resolve it to.
 */
export interface ContextDisplayNames {
  datasets: Record<string, string>;
  files: Record<string, string>;
  collections: Record<string, string>;
  deviceTypes: Record<string, string>;
  sessions: Record<string, string>;
  views: Record<string, string>;
}

interface DerivedContext {
  clientContext: ClientViewingContext;
  displayNames: ContextDisplayNames;
}

export const viewingContextToDerivedContext = (
  viewingContext: ViewingContext,
): DerivedContext => {
  const datasetIds: string[] = [];
  const fileIds: string[] = [];
  const collectionIds: string[] = [];
  const deviceIds: string[] = [];
  const deviceTypeIds: string[] = [];
  const sessionIds: string[] = [];
  const viewIds: string[] = [];
  let tableState: ClientViewingContext["table_state"];
  const datasetDisplayNames: Record<string, string> = {};
  const fileDisplayNames: Record<string, string> = {};
  const collectionDisplayNames: Record<string, string> = {};
  const deviceTypeDisplayNames: Record<string, string> = {};
  const sessionDisplayNames: Record<string, string> = {};
  const viewDisplayNames: Record<string, string> = {};

  // Dataset from dataset page context
  if (viewingContext.dataset?.datasetId) {
    const id = viewingContext.dataset.datasetId;
    datasetIds.push(id);
    const record = viewingContext.dataset.record;
    if (record?.name) {
      datasetDisplayNames[id] = record.name;
    }

    if (
      viewingContext.dataset.selectedFiles &&
      viewingContext.dataset.selectedFiles.length > 0
    ) {
      viewingContext.dataset.selectedFiles.forEach((file) => {
        fileIds.push(file.file_id);
        fileDisplayNames[file.file_id] = file.relative_path;
      });
    }
  }

  // Datasets and files from visualizer context
  for (const source of viewingContext.visualizer?.dataSources ?? []) {
    if (source.type === "dataset") {
      datasetIds.push(source.id);
      if (source.record?.name) {
        datasetDisplayNames[source.id] = source.record.name;
      }
    } else if (source.type === "file") {
      fileIds.push(source.id);
      if (source.record?.relative_path) {
        fileDisplayNames[source.id] = source.record.relative_path;
      }
      // A file's association_id is the parent dataset's ID iff association_type
      // is Dataset; files can also be associated with non-Dataset entities
      // (e.g. nested file structures), so gate before pushing into datasetIds.
      if (
        source.record?.association_id &&
        source.record.association_type === AssociationType.Dataset
      ) {
        datasetIds.push(source.record.association_id);
      }
    }
  }

  if (viewingContext.file?.fileId) {
    fileIds.push(viewingContext.file.fileId);
    if (viewingContext.file.record?.relative_path) {
      fileDisplayNames[viewingContext.file.fileId] =
        viewingContext.file.record.relative_path;
    }
    if (
      viewingContext.file.record?.association_id &&
      viewingContext.file.record.association_type === AssociationType.Dataset
    ) {
      datasetIds.push(viewingContext.file.record.association_id);
    }
  }

  if (viewingContext.collection?.collectionId) {
    const id = viewingContext.collection.collectionId;
    collectionIds.push(id);
    const name = viewingContext.collection.record?.name;
    if (name) {
      collectionDisplayNames[id] = name;
    }
  }

  if (viewingContext.device?.deviceId) {
    deviceIds.push(viewingContext.device.deviceId);
  }

  if (viewingContext.deviceType?.deviceTypeId) {
    const id = viewingContext.deviceType.deviceTypeId;
    deviceTypeIds.push(id);
    const name = viewingContext.deviceType.record?.name;
    if (name) {
      deviceTypeDisplayNames[id] = name;
    }
  }

  if (viewingContext.session?.sessionId) {
    const id = viewingContext.session.sessionId;
    sessionIds.push(id);
    const name = viewingContext.session.record?.name;
    if (name) {
      sessionDisplayNames[id] = name;
    }
  }

  // The table on screen goes whether or not a View is loaded: ad-hoc filters
  // are what "save this as a View" refers to.
  if (viewingContext.resourceTable) {
    const { target, definition, loadedView } = viewingContext.resourceTable;
    tableState = { target, definition };
    if (loadedView) {
      viewIds.push(loadedView.view_id);
      viewDisplayNames[loadedView.view_id] = loadedView.name;
    }
  }

  const dedupe = (ids: string[]) => Array.from(new Set(ids));

  // The board on screen goes under `misc_context.board`, and
  // `misc_context.requests_apply_to` names it as the board the message's
  // requests build or change; the server writes `misc_context` into the
  // message's `<ctx>` block as sent. The board's `id`, `title` and `draft`
  // sit beside the members of the `state` the Boards page registers, so the
  // model and the chip can name the board and the model can tell a board
  // draft from a saved board.
  const board = viewingContext.board;
  const miscContext =
    board === undefined
      ? undefined
      : miscContextForBoard({
          id: board.boardId,
          ...(board.title !== undefined && { title: board.title }),
          draft: board.draft,
          ...board.state,
        });

  return {
    clientContext: {
      dataset_ids: dedupe(datasetIds),
      file_ids: dedupe(fileIds),
      collection_ids: dedupe(collectionIds),
      device_ids: dedupe(deviceIds),
      device_type_ids: dedupe(deviceTypeIds),
      session_ids: dedupe(sessionIds),
      view_ids: dedupe(viewIds),
      table_state: tableState,
      visualizer_state: viewingContext.visualizer?.state,
      misc_context: miscContext,
    },
    displayNames: {
      datasets: datasetDisplayNames,
      files: fileDisplayNames,
      collections: collectionDisplayNames,
      deviceTypes: deviceTypeDisplayNames,
      sessions: sessionDisplayNames,
      views: viewDisplayNames,
    },
  };
};

/**
 * The `ClientViewingContext` id list each list-shaped context type dismisses
 * from. `viz_state`, `table`, and `board` have no entry: each
 * is dismissed as a whole rather than by id. `selected_on_canvas` is absent
 * too: its items live inside `misc_context.board`, not in an id list.
 */
const CONTEXT_TYPE_ID_FIELD = {
  dataset: "dataset_ids",
  file: "file_ids",
  collection: "collection_ids",
  device: "device_ids",
  deviceType: "device_type_ids",
  session: "session_ids",
} as const satisfies Record<
  Exclude<ContextType, "viz_state" | "table" | "board" | "selected_on_canvas">,
  string
>;

export const useClientViewingContext = (): {
  context: ClientViewingContext;
  displayNames: ContextDisplayNames;
  handleContextDelete: (
    contextType: ContextType,
    contextContent: string,
  ) => void;
  /**
   * Resolve the ClientViewingContext for a send: the current derived context
   * (respecting the user's deletions) with live visualizer fields (e.g. the
   * playhead) folded into `visualizer_state` at call time. Live values
   * change at tick rate during playback, so they are deliberately absent
   * from the reactive `context` — call this at the moment a message is sent.
   *
   * Live fields are included only when a workspace resolver is registered
   * AND `visualizer_state` is present in the context (i.e. a visualizer is
   * in context and the user has not dismissed it).
   */
  getContextSnapshot: () => ClientViewingContext;
} => {
  const viewingContext = useViewingContext();

  const [derivedContext, setDerivedContext] = React.useState(() =>
    viewingContextToDerivedContext(viewingContext),
  );

  React.useEffect(() => {
    setDerivedContext(viewingContextToDerivedContext(viewingContext));
  }, [viewingContext]);

  const handleContextDelete = (
    contextType: ContextType,
    contextContent: string,
  ) => {
    setDerivedContext((prev) => {
      // The visualizer is a single blob rather than a list of ids, so it
      // clears wholesale instead of filtering by the deleted item.
      if (contextType === "viz_state") {
        return {
          ...prev,
          clientContext: {
            ...prev.clientContext,
            visualizer_state: undefined,
          },
        };
      }
      // The View and the table it is loaded on are one thing to the user, so
      // dismissing the table chip drops both.
      if (contextType === "table") {
        return {
          ...prev,
          clientContext: {
            ...prev.clientContext,
            view_ids: [],
            table_state: undefined,
          },
        };
      }
      // Dismissing a chip leaves its section or panel out of the next
      // message's `misc_context.board`. The canvas keeps the item selected,
      // and the next change to the viewing context, such as selecting
      // something else, brings it back.
      if (contextType === "selected_on_canvas") {
        return {
          ...prev,
          clientContext: {
            ...prev.clientContext,
            misc_context: withoutCanvasItem(
              prev.clientContext.misc_context,
              contextContent,
            ),
          },
        };
      }
      // `misc_context` holds only the board and `requests_apply_to`, which
      // names it, so dismissing the board's chip clears the field: the next
      // message names no board for its requests and nothing selected on the
      // canvas.
      if (contextType === "board") {
        return {
          ...prev,
          clientContext: {
            ...prev.clientContext,
            misc_context: undefined,
          },
        };
      }

      const idField = CONTEXT_TYPE_ID_FIELD[contextType];
      return {
        ...prev,
        clientContext: {
          ...prev.clientContext,
          [idField]: prev.clientContext[idField].filter(
            (id) => id !== contextContent,
          ),
        },
      };
    });
  };

  // A plain per-render closure (like handleContextDelete above): callers
  // invoke it inside event handlers, so no consumer depends on a stable
  // identity, and closing over this render's derivedContext reads the same
  // deletion-filtered state the reactive `context` exposes.
  const getContextSnapshot = (): ClientViewingContext => {
    const clientContext = derivedContext.clientContext;
    // No visualizer in context (or the user dismissed it) — nothing to
    // enrich. Returning the context unchanged also keeps live state out of
    // messages the user explicitly scoped away from the visualizer.
    if (clientContext.visualizer_state === undefined) {
      return clientContext;
    }
    const liveState = getLiveVisualizerState();
    if (liveState === undefined) {
      return clientContext;
    }
    return {
      ...clientContext,
      visualizer_state: {
        ...clientContext.visualizer_state,
        ...liveState,
      },
    };
  };

  return {
    context: derivedContext.clientContext,
    displayNames: derivedContext.displayNames,
    handleContextDelete,
    getContextSnapshot,
  };
};
